import { normalizeError } from "components/common/error/errorUtils";
import { TenantNotFound } from "components/tenantNotFound";
import { GlobalLoadingScreen } from "contexts/globalLoadingScreen";
import { isConnectionError, isFetchBaseQueryError } from "helpers";
import { getIsMeticulous } from "hooks/permissions";
import { useTenant } from "hooks/settings";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import i18next from "i18next";
import { createContext, ReactNode, use, useEffect, useState } from "react";
import { languages } from "src/constants";
import { backendApi } from "src/store/api/enhancedApi";
import { Tenant } from "src/store/api/generatedApi";
import { GetTenantMetadataResponse } from "src/store/api/generatedZodApi/tenants";
import { changeLanguage } from "src/store/slices/settings";
import { useAppDispatch } from "src/store/store";
import { z } from "zod";

const cachedTenantSchema = GetTenantMetadataResponse.extend({
  compliance_page_version:
    GetTenantMetadataResponse.shape.compliance_page_version.default("legacy"),
  manual_heat_overrides:
    GetTenantMetadataResponse.shape.manual_heat_overrides.default(() => []),
});
const allTenantDataSchema = z.record(z.string(), cachedTenantSchema);

const TenantContext = createContext<Tenant | undefined>(undefined);

export const ProvideTenantData = ({ children }: { children: ReactNode }) => {
  const [allTenantData, setAllTenantData] = useLocalStorageState<
    Record<string, Tenant>
  >("scrapchefTenantData", {}, { schema: allTenantDataSchema });

  const tenantName = useTenant();
  const tenantData = allTenantData[tenantName] ?? null;
  const isLoading = tenantData == null;

  const dispatch = useAppDispatch();
  const isMeticulous = getIsMeticulous();

  const [getTenantMetadata] =
    backendApi.endpoints.getTenantMetadata.useLazyQuery();
  const [failure, setFailure] = useState<{
    tenantName: string;
    error: unknown;
  } | null>(null);
  const error = failure?.tenantName === tenantName ? failure.error : null;

  useEffect(() => {
    let isCurrentRequest = true;
    void getTenantMetadata({ tenantName })
      .unwrap()
      .then((data) => {
        setAllTenantData((current) => ({ ...current, [tenantName]: data }));
      })
      .catch((e: unknown) => {
        console.error(e);
        if (isCurrentRequest) setFailure({ tenantName, error: e });
      });
    return () => {
      isCurrentRequest = false;
      setFailure(null);
    };
  }, [tenantName, getTenantMetadata, setAllTenantData]);

  useEffect(() => {
    if (tenantData) {
      // Only use the default tenant language if running in testing.
      if (isMeticulous) {
        dispatch(changeLanguage(tenantData.default_language));
      }

      // Add translation overrides to i18next resource bundles.
      const translationOverrides = Object.fromEntries(
        Object.entries(tenantData.translation_overrides).map(([key, value]) => [
          key,
          "$t(" + value + ")",
        ])
      );
      Object.keys(languages).forEach((language) =>
        i18next.addResourceBundle(language, tenantName, translationOverrides)
      );
    }
  }, [tenantData, tenantName, dispatch, isMeticulous]);

  const canRenderFromCache = tenantData !== null && isConnectionError(error);
  if (error && !canRenderFromCache) {
    if (
      isFetchBaseQueryError(error) &&
      typeof error.status === "number" &&
      error.status === 404
    ) {
      return <TenantNotFound />;
    }
    if (error instanceof Error) {
      throw error;
    }
    throw normalizeError(error) ?? new Error(JSON.stringify(error));
  }

  if (isLoading) {
    return <GlobalLoadingScreen />;
  }

  return (
    <TenantContext value={allTenantData[tenantName]}>{children}</TenantContext>
  );
};

export const useTenantData = (): Tenant => {
  const tenantData = use(TenantContext);

  if (tenantData === undefined) {
    throw new Error("Tenant data not found");
  }

  return tenantData;
};

export const useTenantDataOptional = (): Partial<Tenant> => {
  const tenantData = use(TenantContext);

  return tenantData ?? {};
};
